import type { ResourceStockpile } from './economyTypes'
import type {
  NationResearchState,
} from './research'
import type { ActiveMajorPowerId } from '../data/regions'
import { ACTIVE_MAJOR_POWER_IDS, type MajorPowerId } from '../data/nations'
import { getNationProfile } from '../data/nationProfiles'
import {
  getSpaceMissionDefinition,
  isSpaceMissionId,
  type SpaceMissionId,
} from '../data/spaceMissions'

export interface ActiveSpaceMission {
  missionId: SpaceMissionId
  totalTurns: number
  turnsRemaining: number
}

export interface NationSpaceProgramState {
  completed: SpaceMissionId[]
  active: ActiveSpaceMission | null
}

export type NationSpaceProgramRecord =
  Record<ActiveMajorPowerId, NationSpaceProgramState>

export type SpaceMissionBlockReason =
  | 'game-not-running'
  | 'already-completed'
  | 'mission-active'
  | 'missing-milestone'
  | 'missing-technologies'
  | 'insufficient-resources'

export interface SpaceMissionAvailability {
  allowed: boolean
  reason?: SpaceMissionBlockReason
  missingTechnologies: string[]
  missingMilestone: SpaceMissionId | null
}

export type StartSpaceMissionResult =
  | {
      started: true
      program: NationSpaceProgramState
      stockpile: ResourceStockpile
      project: ActiveSpaceMission
    }
  | {
      started: false
      program: NationSpaceProgramState
      stockpile: ResourceStockpile
      reason: SpaceMissionBlockReason
      missingTechnologies: string[]
      missingMilestone: SpaceMissionId | null
    }

export interface SpaceMissionCompletion {
  nationId: ActiveMajorPowerId
  missionId: SpaceMissionId
}

export interface SpaceProgramTurnResult {
  spaceProgram: NationSpaceProgramRecord
  completed: SpaceMissionCompletion[]
}

export function createInitialSpaceProgramState(): NationSpaceProgramRecord {
  const spaceProgram = {} as NationSpaceProgramRecord
  for (const id of ACTIVE_MAJOR_POWER_IDS) {
    spaceProgram[id] = { completed: [], active: null }
  }
  return spaceProgram
}

export function getNationMissionPreparationTurns(
  nationId: MajorPowerId,
  missionId: SpaceMissionId,
): number {
  const mission = getSpaceMissionDefinition(missionId)
  const modifier =
    getNationProfile(nationId).missionTurnModifiers[missionId] ?? 0

  return Math.max(1, mission.preparationTurns + modifier)
}

export function getSpaceMissionAvailability(
  program: NationSpaceProgramState,
  research: NationResearchState,
  stockpile: ResourceStockpile,
  missionId: SpaceMissionId,
  gameRunning: boolean,
): SpaceMissionAvailability {
  if (!gameRunning) {
    return {
      allowed: false,
      reason: 'game-not-running',
      missingTechnologies: [],
      missingMilestone: null,
    }
  }

  if (program.completed.includes(missionId)) {
    return {
      allowed: false,
      reason: 'already-completed',
      missingTechnologies: [],
      missingMilestone: null,
    }
  }

  if (program.active !== null) {
    return {
      allowed: false,
      reason: 'mission-active',
      missingTechnologies: [],
      missingMilestone: null,
    }
  }

  const definition = getSpaceMissionDefinition(missionId)
  const missingMilestone =
    definition.prerequisiteMission !== null &&
    !program.completed.includes(definition.prerequisiteMission)
      ? definition.prerequisiteMission
      : null

  if (missingMilestone !== null) {
    return {
      allowed: false,
      reason: 'missing-milestone',
      missingTechnologies: [],
      missingMilestone,
    }
  }

  const missingTechnologies = definition.requiredTechnologies.filter(
    (technologyId) => !research.completed.includes(technologyId),
  )

  if (missingTechnologies.length > 0) {
    return {
      allowed: false,
      reason: 'missing-technologies',
      missingTechnologies,
      missingMilestone: null,
    }
  }

  if (!canAfford(stockpile, definition.cost)) {
    return {
      allowed: false,
      reason: 'insufficient-resources',
      missingTechnologies: [],
      missingMilestone: null,
    }
  }

  return {
    allowed: true,
    missingTechnologies: [],
    missingMilestone: null,
  }
}

export function startSpaceMission(
  nationId: ActiveMajorPowerId,
  program: NationSpaceProgramState,
  research: NationResearchState,
  stockpile: ResourceStockpile,
  missionId: SpaceMissionId,
  gameRunning: boolean,
): StartSpaceMissionResult {
  const availability = getSpaceMissionAvailability(
    program,
    research,
    stockpile,
    missionId,
    gameRunning,
  )

  if (!availability.allowed) {
    return {
      started: false,
      program,
      stockpile,
      reason: availability.reason ?? 'game-not-running',
      missingTechnologies: availability.missingTechnologies,
      missingMilestone: availability.missingMilestone,
    }
  }

  const definition = getSpaceMissionDefinition(missionId)
  const preparationTurns = getNationMissionPreparationTurns(
    nationId,
    missionId,
  )
  const project: ActiveSpaceMission = {
    missionId,
    totalTurns: preparationTurns,
    turnsRemaining: preparationTurns,
  }

  return {
    started: true,
    project,
    program: {
      completed: [...program.completed],
      active: project,
    },
    stockpile: subtractCost(stockpile, definition.cost),
  }
}

export function progressSpaceProgramTurn(
  spaceProgram: NationSpaceProgramRecord,
): SpaceProgramTurnResult {
  const completed: SpaceMissionCompletion[] = []
  const nextSpaceProgram = {} as NationSpaceProgramRecord
  for (const id of ACTIVE_MAJOR_POWER_IDS) {
    const result = progressNationSpaceProgram(spaceProgram[id])
    nextSpaceProgram[id] = result.program
    if (result.completedMissionId) {
      completed.push({ nationId: id, missionId: result.completedMissionId })
    }
  }

  return {
    spaceProgram: nextSpaceProgram,
    completed,
  }
}

export function isNationSpaceProgramRecord(
  value: unknown,
): value is NationSpaceProgramRecord {
  if (!isRecord(value)) {
    return false
  }

  return ACTIVE_MAJOR_POWER_IDS.every((id) =>
    isNationSpaceProgramState(value[id], id),
  )
}

export function migrateLegacySpaceProgramRecord(
  value: unknown,
): NationSpaceProgramRecord | null {
  if (!isRecord(value)) {
    return null
  }

  const starsea = migrateLegacyNationSpaceProgram(
    value.starsea,
    'starsea',
  )
  const redplain = migrateLegacyNationSpaceProgram(
    value.redplain,
    'redplain',
  )

  if (!starsea || !redplain) {
    return null
  }

  return { starsea, redplain }
}

function progressNationSpaceProgram(
  program: NationSpaceProgramState,
): {
  program: NationSpaceProgramState
  completedMissionId: SpaceMissionId | null
} {
  if (program.active === null) {
    return {
      program,
      completedMissionId: null,
    }
  }

  const turnsRemaining = program.active.turnsRemaining - 1

  if (turnsRemaining <= 0) {
    return {
      program: {
        completed: [...program.completed, program.active.missionId],
        active: null,
      },
      completedMissionId: program.active.missionId,
    }
  }

  return {
    program: {
      completed: [...program.completed],
      active: {
        ...program.active,
        turnsRemaining,
      },
    },
    completedMissionId: null,
  }
}

function isNationSpaceProgramState(
  value: unknown,
  nationId: ActiveMajorPowerId,
): value is NationSpaceProgramState {
  if (!isRecord(value) || !Array.isArray(value.completed)) {
    return false
  }

  const completed = new Set<SpaceMissionId>()

  for (const missionId of value.completed) {
    if (!isSpaceMissionId(missionId) || completed.has(missionId)) {
      return false
    }
    completed.add(missionId)
  }

  if (value.active === null) {
    return true
  }

  if (!isRecord(value.active) || !isSpaceMissionId(value.active.missionId)) {
    return false
  }

  if (completed.has(value.active.missionId)) {
    return false
  }

  const expectedTurns = getNationMissionPreparationTurns(
    nationId,
    value.active.missionId,
  )

  return (
    value.active.totalTurns === expectedTurns &&
    typeof value.active.turnsRemaining === 'number' &&
    Number.isInteger(value.active.turnsRemaining) &&
    value.active.turnsRemaining >= 1 &&
    value.active.turnsRemaining <= value.active.totalTurns
  )
}

function migrateLegacyNationSpaceProgram(
  value: unknown,
  nationId: ActiveMajorPowerId,
): NationSpaceProgramState | null {
  if (!isRecord(value) || !Array.isArray(value.completed)) {
    return null
  }

  const completed: SpaceMissionId[] = []
  const seen = new Set<SpaceMissionId>()

  for (const missionId of value.completed) {
    if (!isSpaceMissionId(missionId) || seen.has(missionId)) {
      return null
    }
    seen.add(missionId)
    completed.push(missionId)
  }

  if (value.active === null) {
    return {
      completed,
      active: null,
    }
  }

  if (!isRecord(value.active) || !isSpaceMissionId(value.active.missionId)) {
    return null
  }

  if (seen.has(value.active.missionId)) {
    return null
  }

  const legacyDefinition = getSpaceMissionDefinition(
    value.active.missionId,
  )

  if (
    value.active.totalTurns !== legacyDefinition.preparationTurns ||
    typeof value.active.turnsRemaining !== 'number' ||
    !Number.isInteger(value.active.turnsRemaining) ||
    value.active.turnsRemaining < 1 ||
    value.active.turnsRemaining > value.active.totalTurns
  ) {
    return null
  }

  const newTotalTurns = getNationMissionPreparationTurns(
    nationId,
    value.active.missionId,
  )

  return {
    completed,
    active: {
      missionId: value.active.missionId,
      totalTurns: newTotalTurns,
      turnsRemaining: Math.min(
        value.active.turnsRemaining,
        newTotalTurns,
      ),
    },
  }
}

function canAfford(
  stockpile: ResourceStockpile,
  cost: ResourceStockpile,
): boolean {
  return (
    stockpile.money >= cost.money &&
    stockpile.metal >= cost.metal &&
    stockpile.fuel >= cost.fuel &&
    stockpile.electronics >= cost.electronics
  )
}

function subtractCost(
  stockpile: ResourceStockpile,
  cost: ResourceStockpile,
): ResourceStockpile {
  return {
    money: stockpile.money - cost.money,
    metal: stockpile.metal - cost.metal,
    fuel: stockpile.fuel - cost.fuel,
    electronics: stockpile.electronics - cost.electronics,
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
